import { useCallback, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AddressInput } from '@/components/ui/address-input';
import { Modal } from '@/components/ui/modal';
import { Select } from '@/components/ui/select';
import { FIELD_MAX_LENGTHS } from '@/lib/validation-constants';
import type { Property, CreatePropertyRequest } from '@/types/api';
import { PropertyType } from '@/types/api';
import { BulkUnitsForm, type GeneratedUnit, type RowError } from './bulk-units-form';
import { CsvUploadStep, type CsvRow } from './csv-upload-step';
import type { BulkCreateBuildingsRequest, BulkCreateResponse } from '@/features/buildings/api/buildings-api';
import { cn } from '@/lib/utils';

const PROPERTY_TYPE_OPTIONS = [
  { value: 'Apartment', label: 'Apartment' },
  { value: 'Condo', label: 'Condo' },
  { value: 'StripMall', label: 'Strip Mall' },
  { value: 'Commercial', label: 'Commercial' },
];

// May 14 batch — when onBulkCreate is provided AND we're creating (no
// `property` prop), the modal turns into a 2-step flow: Step 1 is the
// classic property basics, Step 2 adds buildings + units (with prefix +
// auto-increment per building). The whole submit is handled by the
// onBulkCreate callback so callers control the order of property-create
// + buildings-bulk-create + navigation.
//
// Edit mode (`property` non-null) is always single-step and ignores
// onBulkCreate.

interface BuildingDraft {
  // Stable key for React lists — kept independent of `name`.
  key: string;
  name: string;
  // The materialized rows BulkUnitsForm pushes back via onChange.
  units: GeneratedUnit[];
  // CSV-seeded initial units. Passed to BulkUnitsForm via `initialUnits`
  // so the import surfaces as editable preview rows instead of being
  // overwritten by auto-increment defaults.
  seededUnits?: {
    unitNumber: string;
    deviceSerial?: string;
    deviceType?: 'WaterMeter' | 'SALTTransmitter';
    multiplier?: string;
    meterSerial?: string;
    imr?: number;
  }[];
  // Server-side row errors mapped to BulkUnitsForm's preview-row indices.
  unitErrors?: RowError[];
}

export interface PropertyModalBulkCreatePayload {
  propertyBasics: CreatePropertyRequest;
  buildings: BulkCreateBuildingsRequest['buildings'];
}

export interface PropertyModalBulkCreateResult {
  propertyId: string;
  bulk: BulkCreateResponse;
}

export function PropertyModal({
  property,
  initialName,
  onClose,
  onSave,
  onBulkCreate,
  onDelete,
}: {
  property: Property | null;
  initialName?: string;
  onClose: () => void;
  onSave: (data: CreatePropertyRequest) => Promise<void>;
  // Optional 2-step create entry. Receives both halves; resolves with the
  // new property's id + the bulk envelope so the modal can surface
  // row-level errors from the server.
  onBulkCreate?: (payload: PropertyModalBulkCreatePayload) => Promise<PropertyModalBulkCreateResult>;
  onDelete?: () => void;
}) {
  const isEdit = !!property;
  const allowBulk = !isEdit && !!onBulkCreate;

  const [step, setStep] = useState<1 | 2>(1);
  const [formData, setFormData] = useState<CreatePropertyRequest>({
    name: property?.name || initialName || '',
    address: property?.address || '',
    propertyType: property?.propertyType ?? PropertyType.Apartment,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CreatePropertyRequest, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Buildings drafted on Step 2. Empty list is allowed (user submits Step
  // 2 with zero buildings → no Step 2 API call fires).
  const [buildings, setBuildings] = useState<BuildingDraft[]>([]);

  const handleBuildingUnitsChange = useCallback((key: string, units: GeneratedUnit[]) => {
    setBuildings((prev) => prev.map((b) => (b.key === key ? { ...b, units, unitErrors: undefined } : b)));
  }, []);

  function validateStep1(): boolean {
    const newErrors: Partial<Record<keyof CreatePropertyRequest, string>> = {};
    if (!formData.name.trim()) newErrors.name = 'Property name is required.';
    if (!formData.address.trim()) newErrors.address = 'Address is required.';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    return true;
  }

  function addBuilding() {
    const idx = buildings.length;
    setBuildings([
      ...buildings,
      { key: `b-${Date.now()}-${idx}`, name: `Building ${String.fromCharCode(65 + idx)}`, units: [] },
    ]);
  }

  // CSV upload — groups by building name, materializes one BuildingDraft
  // per unique building, and pre-fills the BulkUnitsForm preview rows
  // via the `units` field (BulkUnitsForm pushes them back through its
  // onChange, so the submit payload is the same shape as manual entry).
  function handleCsvParsed(rows: CsvRow[]) {
    const byBuilding = new Map<string, CsvRow[]>();
    rows.forEach((r) => {
      const arr = byBuilding.get(r.building) ?? [];
      arr.push(r);
      byBuilding.set(r.building, arr);
    });
    const next: BuildingDraft[] = [];
    Array.from(byBuilding.entries()).forEach(([buildingName, items], i) => {
      next.push({
        key: `csv-${Date.now()}-${i}`,
        name: buildingName,
        units: [],
        seededUnits: items.map((r) => ({
          unitNumber: r.unit,
          deviceSerial: r.deviceSerial,
          deviceType: r.deviceType,
          multiplier: r.multiplier,
          meterSerial: r.meterSerial,
          imr: r.imr,
        })),
      });
    });
    setBuildings(next);
    setSubmitError(null);
  }

  function removeBuilding(key: string) {
    setBuildings((prev) => prev.filter((b) => b.key !== key));
  }

  function setBuildingName(key: string, name: string) {
    setBuildings((prev) => prev.map((b) => (b.key === key ? { ...b, name } : b)));
  }

  async function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateStep1()) return;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await onSave(formData);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleStep1Continue() {
    if (!validateStep1()) return;
    setSubmitError(null);
    setStep(2);
  }

  // Step 2 submit — POST /api/properties first, then /api/buildings/bulk
  // if the user has actually added any. onBulkCreate handles both halves
  // so the parent can manage cache invalidation + navigation.
  async function handleCreateSubmit() {
    if (!onBulkCreate) return; // unreachable when allowBulk is true
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const buildingsPayload = buildings
        .filter((b) => b.name.trim().length > 0)
        .map((b) => ({
          name: b.name.trim(),
          units: b.units
            .filter((u) => u.unitNumber.trim().length > 0)
            .map((u) => ({
              unitNumber: u.unitNumber.trim(),
              deviceSerial: u.deviceSerial?.trim() || undefined,
              deviceType: u.deviceType,
              multiplier: u.multiplier,
              meterSerial: u.meterSerial,
              imr: u.imr,
            })),
        }));
      const res = await onBulkCreate({
        propertyBasics: formData,
        buildings: buildingsPayload,
      });
      if (!res.bulk.ok && res.bulk.errorBody) {
        // Map flat server-error indices back to per-building preview rows.
        // The server's flat index is: building header (+1) per building,
        // then its units. We rebuild that walk so unit errors land on
        // the right row inside the right BulkUnitsForm.
        let cursor = 0;
        const nextBuildings = buildings.map<BuildingDraft>((b) => {
          const start = cursor;
          cursor += 1 + b.units.length; // header + units
          const myErrors: RowError[] = [];
          (res.bulk.errorBody?.errors ?? []).forEach((e) => {
            if (e.index > start && e.index < start + 1 + b.units.length) {
              myErrors.push({ rowIndex: e.index - start - 1, message: e.message });
            }
          });
          return { ...b, unitErrors: myErrors };
        });
        setBuildings(nextBuildings);
        setSubmitError('Some rows have problems — see highlighted entries.');
        return;
      }
      onClose();
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  // Single-step EDIT view (and create when bulk is not enabled)
  if (isEdit || !allowBulk) {
    return (
      <Modal
        isOpen
        onClose={onClose}
        title={property ? 'Edit Property' : 'Add New Property'}
        description="Enter the property details below"
      >
        <form noValidate onSubmit={handleEditSubmit} className="space-y-4">
          <Input
            label="Property Name"
            required
            maxLength={FIELD_MAX_LENGTHS.PROPERTY_NAME}
            value={formData.name}
            onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setErrors(prev => ({ ...prev, name: undefined })); setSubmitError(null); }}
            placeholder="e.g., Riverside Apartments"
            error={errors.name}
          />
          <AddressInput
            label="Address"
            required
            maxLength={FIELD_MAX_LENGTHS.PROPERTY_ADDRESS}
            value={formData.address}
            onChange={(val) => { setFormData({ ...formData, address: val }); setErrors(prev => ({ ...prev, address: undefined })); setSubmitError(null); }}
            placeholder="Start typing an address..."
            error={errors.address}
          />
          <Select
            label="Property Type"
            required
            value={formData.propertyType}
            onChange={(val) => { setFormData({ ...formData, propertyType: val as PropertyType }); setSubmitError(null); }}
            options={PROPERTY_TYPE_OPTIONS}
          />
          {submitError && <p className="text-sm text-error-600">{submitError}</p>}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" isLoading={isSubmitting} className="flex-1">
              {property ? 'Update' : 'Create'} Property
            </Button>
          </div>
          {property && onDelete && (
            <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="button" variant="danger" className="w-full" onClick={onDelete}>
                Delete Property
              </Button>
            </div>
          )}
        </form>
      </Modal>
    );
  }

  // 2-STEP CREATE view
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={step === 1 ? 'Add New Property' : 'Add Buildings & Units'}
      description={
        step === 1
          ? 'Start with the basics — name, address, and type.'
          : `Optional — add buildings and units to ${formData.name || 'your property'} now, or skip and add them later.`
      }
      size="lg"
    >
      {/* Step indicator — numbered circles + animated connector so the
          title / description don't have to repeat "Step X of 2". The
          connector fills with the brand color once step 1 completes,
          giving the user a clear visual of progress through the flow. */}
      <div className="flex items-center gap-3 mb-6">
        <StepDot index={1} active={step === 1} done={step > 1} label="Property" />
        <div className="flex-1 h-0.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full bg-primary-600 dark:bg-primary-500 transition-all duration-300',
              step > 1 ? 'w-full' : 'w-0',
            )}
          />
        </div>
        <StepDot index={2} active={step === 2} done={false} label="Buildings & Units" />
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <Input
            label="Property Name"
            required
            maxLength={FIELD_MAX_LENGTHS.PROPERTY_NAME}
            value={formData.name}
            onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setErrors(prev => ({ ...prev, name: undefined })); setSubmitError(null); }}
            placeholder="e.g., Riverside Apartments"
            error={errors.name}
          />
          <AddressInput
            label="Address"
            required
            maxLength={FIELD_MAX_LENGTHS.PROPERTY_ADDRESS}
            value={formData.address}
            onChange={(val) => { setFormData({ ...formData, address: val }); setErrors(prev => ({ ...prev, address: undefined })); setSubmitError(null); }}
            placeholder="Start typing an address..."
            error={errors.address}
          />
          <Select
            label="Property Type"
            required
            value={formData.propertyType}
            onChange={(val) => { setFormData({ ...formData, propertyType: val as PropertyType }); setSubmitError(null); }}
            options={PROPERTY_TYPE_OPTIONS}
          />
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="button" onClick={handleStep1Continue} className="flex-1">
              Continue
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <CsvUploadStep
            onParsed={(result) => handleCsvParsed(result.rows)}
            onError={(msg) => setSubmitError(msg)}
          />

          {buildings.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Add buildings and units manually, upload a CSV above, or skip this step and add them later from the Property Portal.
              </p>
            </div>
          ) : (
            <ul className="space-y-4">
              {buildings.map((b) => (
                <li
                  key={b.key}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50/40 dark:bg-gray-900/30"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Input
                      label="Building name"
                      value={b.name}
                      onChange={(e) => setBuildingName(b.key, e.target.value)}
                      placeholder="e.g., Trailer A"
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeBuilding(b.key)}
                      className="text-gray-400 hover:text-error-600 p-2 rounded transition-colors self-end"
                      title="Remove building"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <BulkUnitsForm
                    buildingName={b.name}
                    initialUnits={b.seededUnits}
                    onChange={(units) => handleBuildingUnitsChange(b.key, units)}
                    externalErrors={b.unitErrors}
                  />
                </li>
              ))}
            </ul>
          )}

          <Button type="button" variant="outline" onClick={addBuilding} disabled={buildings.length >= 20}>
            <Plus className="w-4 h-4" />
            Add Building
          </Button>

          {submitError && <p className="text-sm text-error-600">{submitError}</p>}

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setStep(1)}>
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="flex-1" />
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="button" onClick={handleCreateSubmit} isLoading={isSubmitting}>
              {buildings.length === 0 ? 'Create Property' : 'Create & Add Units'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function StepDot({ index, active, done, label }: { index: number; active: boolean; done: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span
        className={cn(
          'inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold flex-shrink-0 transition-colors',
          active && 'bg-primary-600 text-white ring-4 ring-primary-100 dark:ring-primary-500/20',
          done && 'bg-success-600 text-white',
          !active && !done && 'bg-transparent text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600',
        )}
      >
        {done ? <Check className="w-3.5 h-3.5" /> : index}
      </span>
      <span
        className={cn(
          'text-sm whitespace-nowrap',
          active && 'text-gray-900 dark:text-gray-100 font-medium',
          done && 'text-gray-700 dark:text-gray-300',
          !active && !done && 'text-gray-500 dark:text-gray-400',
        )}
      >
        {label}
      </span>
    </div>
  );
}
